/** @jsx jsx */
import 'react-hot-loader';
import ReactDOM from 'react-dom';
import { applyMiddleware, compose, createStore } from 'redux';
import createSagaMiddleware from 'redux-saga';
import logger from 'redux-logger';
import moment from 'moment';
import { Cookie, getCacheLanguage } from 'shared/utils';
import socket from 'shared/config/socket';
import { COOKIE_TAG } from 'shared/constants';
import 'moment/locale/eu';
import 'moment/locale/vi';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import { ConfigProvider } from 'antd';
import enGB from 'antd/es/locale/en_GB';
import viVN from 'antd/es/locale/vi_VN';
import jaJp from 'antd/es/locale/ja_JP';
import { BrowserRouter as Router } from 'react-router-dom';
import ErrorBoundary from 'components/Error';
import { jsx, ThemeProvider } from '@emotion/react';
import theme from 'shared/styles/theme';
import GlobalContext from './globalContext';
import reducers, { initState } from './reducers';
import saga from './saga';
import Routes from './routes/root';

require('../public/assets/styles/index.scss');

const sagaMiddleware = createSagaMiddleware();
const cache = Cookie.get(COOKIE_TAG);

const defaultState = {
  ...initState,
  application: {
    ...initState.application,
  },
};

moment.locale(cache?.language ?? 'vi');

let middleware = [sagaMiddleware, socket()];

if (process.env.NODE_ENV === 'development') {
  middleware = [...middleware, logger];
}

const enhancers = [applyMiddleware(...middleware)];

const composeEnhancers =
  process.env.NODE_ENV === 'development' && typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({ shouldHotReload: false })
    : compose;

export const store = createStore(reducers, defaultState, composeEnhancers(...enhancers));

sagaMiddleware.run(saga);

const Root = (props) => {
  const { store: storeData } = props;

  const getLanguage = () => {
    if (getCacheLanguage() === 'en') return enGB;
    if (getCacheLanguage() === 'jp') return jaJp;
    return viVN;
  };

  return (
    <ThemeProvider theme={theme}>
      <Provider store={storeData}>
        <ConfigProvider locale={getLanguage()}>
          <ErrorBoundary>
            <GlobalContext>
              <Router>
                <div css={{ height: '100%', overflow: 'hidden' }}>
                  <Routes />
                </div>
              </Router>
            </GlobalContext>
          </ErrorBoundary>
        </ConfigProvider>
      </Provider>
    </ThemeProvider>
  );
};

Root.propTypes = {
  store: PropTypes.objectOf(PropTypes.any),
};

Root.defaultProps = {
  store: {},
};

ReactDOM.render(<Root store={store} />, document.getElementById('root'));
